<template>
  <HrPageLayout
    title="Employee Promotions"
    subtitle="Manage employee promotions and career progression"
    icon="📈"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Promotions' }]"
  >
    <template #header-actions>
      <div class="hr-flex hr-flex-center hr-gap-2">
        <button type="button" @click="exportPromotions" class="hr-btn hr-btn-outline hr-btn-secondary">
          <i class="bx bx-export"></i> Export
        </button>
        <button type="button" @click="refreshData" class="hr-btn hr-btn-outline hr-btn-secondary">
          <i class="bx bx-refresh"></i> Refresh
        </button>
        <button type="button" @click="createPromotion" class="hr-btn hr-btn-primary">
          <i class="bx bx-plus-circle"></i> Create Promotion
        </button>
      </div>
    </template>

    <div class="promotions-page">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-trending-up"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total promotions</p>
          <h2 class="hr-metric-value">{{ metrics.totalPromotions || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Pending approval</p>
          <h2 class="hr-metric-value">{{ metrics.pendingPromotions || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Approved</p>
          <h2 class="hr-metric-value">{{ metrics.approvedPromotions || 0 }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-calendar-event"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Upcoming (30 days)</p>
          <h2 class="hr-metric-value">{{ metrics.upcomingPromotions || 0 }}</h2>
            </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="promo-filters">
            <div class="hr-filter-group">
              <label class="hr-form-label">Promotion type</label>
              <select v-model="filters.promotionType" class="hr-select">
                <option value="">All types</option>
                <option v-for="type in promotionTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.departmentId" class="hr-select">
                <option value="">All departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">From</label>
              <input v-model="filters.startDate" type="date" class="hr-input">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">To</label>
              <input v-model="filters.endDate" type="date" class="hr-input">
            </div>
            <div class="hr-filter-group promo-search">
              <label class="hr-form-label">Search</label>
              <div class="hr-search-wrap">
                <input v-model="filters.search" type="search" class="hr-input" placeholder="Name, designation, code…">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
            <button type="button" class="hr-btn hr-btn-outline hr-btn-secondary" @click="resetFilters">
              <i class="bx bx-reset"></i> Reset
            </button>
          </div>
        </div>
      </div>

      <div class="promo-insights-grid hr-mb-4">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt-2"></i> Promotions by type</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="promo-type-list">
              <div v-for="(count, type) in metrics.typeCounts" :key="type" class="promo-type-row">
                <div class="promo-type-meta">
                  <span class="hr-badge" :class="getPromotionTypeColor(type)">{{ type }}</span>
                  <span class="promo-type-count">{{ count }}</span>
                </div>
                <div class="hr-progress-bar">
                  <div
                    class="hr-progress-fill"
                    :class="getPromotionTypeColor(type)"
                    :style="{ width: getPromotionTypePercentage(type) + '%' }"
                  ></div>
                </div>
                <span class="promo-type-pct">{{ getPromotionTypePercentage(type) }}%</span>
              </div>
            </div>
            <div v-else class="hr-empty-state compact">
              <i class="bx bx-bar-chart-alt-2"></i>
              <p>No type breakdown for this period</p>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-calendar"></i> Upcoming effective dates</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="upcomingPromotions.length > 0" class="promo-upcoming-list">
              <div v-for="promotion in upcomingPromotions" :key="promotion.id" class="promo-upcoming-item">
                <div class="promo-date-chip">
                  <span class="promo-date-month">{{ formatDateMonth(promotion.effectiveDate) }}</span>
                  <span class="promo-date-day">{{ formatDateDay(promotion.effectiveDate) }}</span>
                </div>
                <div class="promo-upcoming-body">
                  <div class="promo-upcoming-name">{{ promotion.employeeName }}</div>
                  <div class="promo-career-path">
                    <span>{{ promotion.previousDesignation }}</span>
                    <i class="bx bx-right-arrow-alt"></i>
                    <strong>{{ promotion.newDesignation }}</strong>
                  </div>
                  <small v-if="promotion.newDepartmentName" class="promo-dept">
                    <i class="bx bx-buildings"></i> {{ promotion.newDepartmentName }}
                  </small>
                </div>
                <span class="hr-badge" :class="getPromotionTypeColor(promotion.promotionType)">{{ promotion.promotionType }}</span>
              </div>
            </div>
            <div v-else class="hr-empty-state compact">
              <i class="bx bx-calendar-check"></i>
              <p>No promotions scheduled in the next 30 days</p>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> All promotions</h5>
          <span v-if="!loading && promotions.length" class="hr-text-muted hr-small">
            {{ pagination.totalPromotions }} record{{ pagination.totalPromotions === 1 ? '' : 's' }}
          </span>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading promotions…</p>
          </div>

          <div v-else-if="!promotions.length" class="hr-empty-state">
            <i class="bx bx-briefcase-alt"></i>
            <h5>No promotions found</h5>
            <p>Adjust filters or create a new promotion request.</p>
            <button type="button" @click="createPromotion" class="hr-btn hr-btn-primary">
              <i class="bx bx-plus"></i> Create promotion
            </button>
          </div>

          <template v-else>
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Career change</th>
                    <th>Timeline</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th class="hr-text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="promotion in promotions" :key="promotion.id">
                    <td>
                      <div class="promo-emp-cell">
                        <div class="promo-avatar">{{ getInitials(promotion.employeeName) }}</div>
                        <div>
                          <div class="promo-emp-name">{{ promotion.employeeName }}</div>
                          <code class="promo-code">{{ promotion.promotionCode }}</code>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div class="promo-career-path table-path">
                        <span class="promo-from">{{ promotion.previousDesignation }}</span>
                        <i class="bx bx-right-arrow-alt"></i>
                        <span class="promo-to">{{ promotion.newDesignation }}</span>
                      </div>
                      <div
                        v-if="promotion.previousDepartmentName !== promotion.newDepartmentName"
                        class="promo-dept-change"
                      >
                        <i class="bx bx-transfer"></i>
                        {{ promotion.previousDepartmentName }} → {{ promotion.newDepartmentName }}
                      </div>
                    </td>
                    <td>
                      <div class="promo-dates">
                        <span><i class="bx bx-edit"></i> {{ formatDate(promotion.promotionDate) }}</span>
                        <span class="promo-effective"><i class="bx bx-calendar"></i> {{ formatDate(promotion.effectiveDate) }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getPromotionTypeColor(promotion.promotionType)">
                        {{ promotion.promotionType }}
                      </span>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getStatusBadgeClass(promotion.status)">
                        {{ promotion.status }}
                      </span>
                    </td>
                    <td class="hr-text-end">
                      <div class="promo-actions">
                        <button type="button" class="hr-btn hr-btn-sm hr-btn-icon hr-btn-info" title="View" @click="viewPromotionDetails(promotion)">
                          <i class="bx bx-show"></i>
                        </button>
                        <button
                          v-if="promotion.status === 'Pending'"
                          type="button"
                          class="hr-btn hr-btn-sm hr-btn-icon hr-btn-secondary"
                          title="Edit"
                          @click="editPromotion(promotion)"
                        >
                          <i class="bx bx-edit"></i>
                        </button>
                        <button
                          v-if="canApprove(promotion)"
                          type="button"
                          class="hr-btn hr-btn-sm hr-btn-icon hr-btn-success"
                          title="Approve"
                          @click="approvePromotion(promotion)"
                        >
                          <i class="bx bx-check"></i>
                        </button>
                        <button
                          v-if="promotion.status === 'Pending'"
                          type="button"
                          class="hr-btn hr-btn-sm hr-btn-icon hr-btn-danger"
                          title="Reject"
                          @click="rejectPromotion(promotion)"
                        >
                          <i class="bx bx-x"></i>
                        </button>
                        <button
                          v-if="promotion.status === 'Approved'"
                          type="button"
                          class="hr-btn hr-btn-sm hr-btn-icon hr-btn-primary"
                          title="Complete"
                          @click="completePromotion(promotion)"
                        >
                          <i class="bx bx-check-double"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="pagination.totalPages > 1" class="hr-pagination">
              <div class="hr-pagination-info">
                Page {{ pagination.currentPage }} of {{ pagination.totalPages }}
                ({{ pagination.totalPromotions }} total)
              </div>
              <nav class="hr-pagination-nav">
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage <= 1" @click="changePage(pagination.currentPage - 1)">
                  Previous
                </button>
                <button
                  v-for="page in getPageNumbers()"
                  :key="'p-' + page"
                  type="button"
                  class="hr-page-btn"
                  :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                  :disabled="page === '...'"
                  @click="page !== '...' && changePage(page)"
                >
                  {{ page }}
                </button>
                <button type="button" class="hr-page-btn" :disabled="pagination.currentPage >= pagination.totalPages" @click="changePage(pagination.currentPage + 1)">
                  Next
                </button>
              </nav>
            </div>
          </template>
        </div>
      </div>
    </div>

    <ModalWrapper ref="promotionModal" :title="isEditMode ? 'Edit Promotion' : 'Create New Promotion'" size="large">
      <template #body>
            <form class="promo-form" @submit.prevent="submitPromotion">
              <div class="row g-3">
                <!-- Employee Selection (when creating) -->
                <div v-if="!isEditMode" class="col-md-12">
                  <label class="hr-form-label">Select employee <span class="required">*</span></label>
                  <div v-if="!promotionForm.employeeId" class="promo-employee-picker">
                    <input
                      v-model="employeeSearch"
                      type="text"
                      class="hr-input"
                      placeholder="Search by name, employee no, or ID..."
                      autocomplete="off"
                      @input="debouncedEmployeeSearch"
                      @focus="onEmployeeSearchFocus"
                    />
                    <p v-if="employeeSearchLoading" class="promo-search-hint hr-mt-1">
                      <span class="hr-spinner small"></span> Searching...
                    </p>
                    <p v-else-if="employeeSearch && !eligibleEmployees.length" class="promo-search-hint hr-mt-1">
                      No eligible employees match your search.
                    </p>
                    <p v-else-if="!employeeSearch && !eligibleEmployees.length" class="promo-search-hint hr-mt-1">
                      Type to search, or pick from the list below.
                    </p>
                    <div
                      v-if="eligibleEmployees.length"
                      class="promo-search-results"
                      role="listbox"
                    >
                      <button
                        v-for="employee in eligibleEmployees"
                        :key="employee.id"
                        type="button"
                        class="promo-search-item"
                        role="option"
                        @mousedown.prevent
                        @click="selectEligibleEmployee(employee)"
                      >
                        <strong>{{ employee.name }}</strong>
                        <span>{{ employee.designation || 'No designation' }} · {{ employee.departmentName || 'No department' }}</span>
                      </button>
                    </div>
                  </div>
                  <div v-else class="promo-selected-card hr-mt-1">
                    <div class="promo-selected-row">
                      <div>
                        <strong>{{ selectedEmployee?.name }}</strong>
                        <div class="hr-text-muted hr-small">
                          {{ selectedEmployee?.designation }} · {{ selectedEmployee?.departmentName }}
                        </div>
                      </div>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline hr-btn-secondary" @click="clearSelectedEmployee">
                        Change
                      </button>
                    </div>
                  </div>
                  <small class="text-muted d-block hr-mt-1">
                    <i class="mdi mdi-information-outline me-1"></i>
                    Only employees with at least 1 year of service are eligible
                  </small>
                </div>

                <!-- Employee details (when selected or editing) -->
                <div v-if="selectedEmployee" class="col-md-12">
                  <div class="promo-employee-card">
                      <h6 class="promo-employee-card-title">Current employment details</h6>
                      <div class="row">
                        <div class="col-md-6">
                          <p class="mb-1"><strong>Name:</strong> {{ selectedEmployee.name }}</p>
                          <p class="mb-1"><strong>Designation:</strong> {{ selectedEmployee.designation }}</p>
                          <p class="mb-1"><strong>Department:</strong> {{ selectedEmployee.departmentName }}</p>
                        </div>
                        <div class="col-md-6">
                          <p class="mb-1">
                            <strong>Current Salary:</strong>
                            <span
                              :class="['salary-value', {'blurred': !isSalaryRevealed('selected-employee-salary'), 'revealed': isSalaryRevealed('selected-employee-salary')}]"
                              @click="toggleSalaryVisibility('selected-employee-salary')"
                            >
                              {{ formatSalary(selectedEmployee.salary, 'selected-employee-salary') }}
                            </span>
                          </p>
                          <p class="mb-1"><strong>Years of Service:</strong> {{ selectedEmployee.serviceYears || calculateServiceYears(selectedEmployee.hireDate) }}</p>
                        </div>
                      </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Current designation</label>
                  <input v-model="promotionForm.previousDesignation" type="text" class="hr-input" readonly>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">New designation <span class="required">*</span></label>
                  <input v-model="promotionForm.newDesignation" type="text" class="hr-input" required>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Current department</label>
                  <input v-model="promotionForm.previousDepartmentName" type="text" class="hr-input" readonly>
                  <input type="hidden" v-model="promotionForm.previousDepartmentId">
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">New department</label>
                  <select v-model="promotionForm.newDepartmentId" class="hr-select">
                    <option :value="promotionForm.previousDepartmentId">{{ promotionForm.previousDepartmentName }} (Same)</option>
                    <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                      {{ dept.departmentName }}
                    </option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Current salary</label>
                  <div class="promo-salary-row">
                    <input v-model="promotionForm.previousSalary" type="number" class="hr-input" readonly>
                    <button type="button" class="hr-btn hr-btn-outline hr-btn-secondary" @click="toggleSalaryVisibility('promotion-previous-salary')">
                      <i class="bx" :class="isSalaryRevealed('promotion-previous-salary') ? 'bx-hide' : 'bx-show'"></i>
                    </button>
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">New salary <span class="required">*</span></label>
                  <div class="promo-salary-row">
                    <input v-model="promotionForm.newSalary" type="number" min="0" step="100" class="hr-input" required>
                    <button type="button" class="hr-btn hr-btn-outline hr-btn-secondary" @click="toggleSalaryVisibility('promotion-new-salary')">
                      <i class="bx" :class="isSalaryRevealed('promotion-new-salary') ? 'bx-hide' : 'bx-show'"></i>
                    </button>
                  </div>
                  <small v-if="calculateSalaryIncrease() > 0" class="promo-salary-up">+{{ calculateSalaryIncrease() }}% increase</small>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Promotion date <span class="required">*</span></label>
                  <input v-model="promotionForm.promotionDate" type="date" class="hr-input" required>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Effective date <span class="required">*</span></label>
                  <input v-model="promotionForm.effectiveDate" type="date" class="hr-input" required>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Promotion type <span class="required">*</span></label>
                  <select v-model="promotionForm.promotionType" class="hr-select" required>
                    <option value="" disabled>Select Promotion Type</option>
                    <option v-for="type in promotionTypes" :key="type" :value="type">{{ type }}</option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label class="hr-form-label">Performance rating</label>
                  <select v-model="promotionForm.performanceRating" class="hr-select">
                    <option value="">Not Specified</option>
                    <option v-for="rating in [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]" :key="rating" :value="rating">
                      {{ rating }} {{ rating >= 4 ? '(Excellent)' : rating >= 3 ? '(Good)' : rating >= 2 ? '(Average)' : '(Poor)' }}
                    </option>
                  </select>
                </div>

                <div class="col-md-12">
                  <label class="hr-form-label">Reason for promotion <span class="required">*</span></label>
                  <textarea v-model="promotionForm.reason" class="hr-input" rows="3" required></textarea>
                </div>

                <div class="col-md-12">
                  <label class="hr-form-label">Recommendations</label>
                  <textarea v-model="promotionForm.recommendations" class="hr-input" rows="2"></textarea>
                </div>

                <div class="col-md-12">
                  <label class="hr-form-label">Supporting documents</label>
                  <input type="file" class="hr-input" multiple ref="documentFiles" @change="handleFileSelection">
                  <small class="text-muted">Performance reviews, certificates, approval emails, etc.</small>
                </div>

                <!-- Display existing documents when editing -->
                <div v-if="promotionForm.documentsList && promotionForm.documentsList.length > 0" class="col-md-12">
                  <label class="hr-form-label">Existing documents</label>
                  <div class="promo-doc-list">
                    <div v-for="(doc, index) in promotionForm.documentsList" :key="index" class="promo-doc-item">
                      <span><i class="bx bx-file"></i> {{ doc.name }}</span>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click="removeDocument(doc, index)">
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="closePromotionModal">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" @click="submitPromotion">
          {{ isEditMode ? 'Update promotion' : 'Create promotion' }}
        </button>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="detailsModal" title="Promotion details" size="large">
      <template #body>
            <div v-if="selectedPromotion">
              <div class="row mb-4">
                <div class="col-md-8">
                  <h4 class="font-size-16">{{ selectedPromotion.employeeName }}</h4>
                  <p class="text-muted mb-1">
                    <span class="badge rounded-pill" :class="getPromotionTypeColor(selectedPromotion.promotionType)">
                      {{ selectedPromotion.promotionType }}
                    </span>
                  </p>
                  <p class="text-muted mb-0">Promotion Code: {{ selectedPromotion.promotionCode }}</p>
                </div>
                <div class="col-md-4 text-md-end">
                  <span class="badge" :class="getStatusBadgeClass(selectedPromotion.status)">
                    {{ selectedPromotion.status }}
                  </span>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Promotion Information</h6>
                    </div>
                    <div class="card-body">
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-calendar me-2"></i>Promotion Date:</span>
                            <span class="text-muted">{{ formatDate(selectedPromotion.promotionDate) }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-calendar-clock me-2"></i>Effective Date:</span>
                            <span class="text-muted">{{ formatDate(selectedPromotion.effectiveDate) }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-briefcase-outline me-2"></i>Previous Designation:</span>
                            <span class="text-muted">{{ selectedPromotion.previousDesignation }}</span>
                          </div>
                        </li>
                        <li class="list-group-item">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-briefcase-upload me-2"></i>New Designation:</span>
                            <span class="text-muted">{{ selectedPromotion.newDesignation }}</span>
                          </div>
                        </li>
                        <li class="list-group-item" v-if="selectedPromotion.previousDepartmentName || selectedPromotion.newDepartmentName">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-office-building me-2"></i>Department:</span>
                            <span class="text-muted">
                              {{ selectedPromotion.previousDepartmentName }}
                              <i v-if="selectedPromotion.previousDepartmentName !== selectedPromotion.newDepartmentName" class="mdi mdi-arrow-right mx-1"></i>
                              {{ selectedPromotion.previousDepartmentName !== selectedPromotion.newDepartmentName ? selectedPromotion.newDepartmentName : '' }}
                            </span>
                          </div>
                        </li>
                        <li class="list-group-item" v-if="selectedPromotion.previousSalary || selectedPromotion.newSalary">
                          <div class="d-flex justify-content-between">
                            <span><i class="mdi mdi-currency-sign me-2"></i>Salary:</span>
                            <span class="text-muted">
                              <span
                                :class="['salary-value', {'blurred': !isSalaryRevealed('details-previous-salary'), 'revealed': isSalaryRevealed('details-previous-salary')}]"
                                @click="toggleSalaryVisibility('details-previous-salary')"
                              >
                                {{ formatSalary(selectedPromotion.previousSalary, 'details-previous-salary') }}
                              </span>
                              <i v-if="selectedPromotion.previousSalary && selectedPromotion.newSalary" class="mdi mdi-arrow-right mx-1"></i>
                              <span
                                v-if="selectedPromotion.newSalary"
                                :class="['salary-value', {'blurred': !isSalaryRevealed('details-new-salary'), 'revealed': isSalaryRevealed('details-new-salary')}]"
                                @click="toggleSalaryVisibility('details-new-salary')"
                              >
                                {{ formatSalary(selectedPromotion.newSalary, 'details-new-salary') }}
                              </span>
                              <span v-if="calculateSalaryIncreaseFromValues(selectedPromotion.previousSalary, selectedPromotion.newSalary) > 0" class="badge bg-success ms-1">
                                +{{ calculateSalaryIncreaseFromValues(selectedPromotion.previousSalary, selectedPromotion.newSalary) }}%
                              </span>
                            </span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card border h-100">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Reason & Recommendations</h6>
                    </div>
                    <div class="card-body">
                      <h6 class="font-size-14 mb-2">Reason for Promotion</h6>
                      <p v-if="selectedPromotion.reason">{{ selectedPromotion.reason }}</p>
                      <p v-else class="text-muted text-center">No reason provided</p>

                      <div v-if="selectedPromotion.recommendations" class="mt-3">
                        <h6 class="font-size-14 mb-2">Recommendations</h6>
                        <p>{{ selectedPromotion.recommendations }}</p>
                      </div>

                      <div v-if="selectedPromotion.performanceRating" class="mt-3">
                        <h6 class="font-size-14 mb-2">Performance Rating</h6>
                        <div class="d-flex align-items-center">
                          <span class="me-2">{{ selectedPromotion.performanceRating }}/5</span>
                          <div class="progress flex-grow-1" style="height: 6px;">
                            <div
                              class="progress-bar"
                              :class="getPerformanceRatingColor(selectedPromotion.performanceRating)"
                              :style="`width: ${(selectedPromotion.performanceRating / 5) * 100}%`"
                              role="progressbar"
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Approval Workflow Section -->
              <div class="row mt-4" v-if="selectedPromotion.approvals && selectedPromotion.approvals.length > 0">
                <div class="col-md-12">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Approval Workflow</h6>
                    </div>
                    <div class="card-body">
                      <div class="promotion-approval-timeline">
                        <div
                          v-for="(approval, index) in selectedPromotion.approvals"
                          :key="approval.id"
                          class="approval-step d-flex mb-3"
                        >
                          <div class="approval-step-icon me-3">
                            <div
                              class="rounded-circle d-flex align-items-center justify-content-center"
                              :class="getApprovalStatusClass(approval.status)"
                              style="width: 40px; height: 40px;"
                            >
                              <i
                                class="mdi"
                                :class="getApprovalStatusIcon(approval.status)"
                                style="font-size: 20px;"
                              ></i>
                            </div>
                            <div
                              v-if="index < selectedPromotion.approvals.length - 1"
                              class="approval-connector"
                              :class="getApprovalStatusClass(approval.status, true)"
                            ></div>
                          </div>
                          <div class="approval-step-content flex-grow-1">
                            <h6 class="mb-1">{{ approval.approverRole }}</h6>
                            <p class="text-muted mb-1">
                              <span
                                class="badge"
                                :class="getStatusBadgeClass(approval.status)"
                              >
                                {{ approval.status }}
                              </span>
                              <span
                                v-if="approval.approverName"
                                class="ms-2"
                              >
                                by {{ approval.approverName }}
                              </span>
                              <span
                                v-if="approval.approvedAt"
                                class="ms-2"
                              >
                                ({{ formatDateTime(approval.approvedAt) }})
                              </span>
                            </p>
                            <p v-if="approval.comments" class="mt-1 mb-0 text-muted fst-italic">
                              "{{ approval.comments }}"
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Documents Section -->
              <div class="row mt-4" v-if="selectedPromotion.documentsList && selectedPromotion.documentsList.length > 0">
                <div class="col-md-12">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Supporting Documents</h6>
                    </div>
                    <div class="card-body">
                      <div class="row">
                        <div
                          v-for="(doc, index) in selectedPromotion.documentsList"
                          :key="index"
                          class="col-md-4 col-sm-6 mb-3"
                        >
                          <div class="border rounded p-3 h-100 d-flex flex-column">
                            <div class="d-flex align-items-center mb-2">
                              <i
                                class="mdi mdi-file-document-outline me-2"
                                style="font-size: 24px;"
                              ></i>
                              <div class="text-truncate">{{ doc.name }}</div>
                            </div>
                            <div class="mt-auto text-end">
                              <a
                                :href="doc.path"
                                target="_blank"
                                class="btn btn-sm btn-info"
                              >
                                <i class="mdi mdi-download me-1"></i> View
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activity History -->
              <div class="row mt-4" v-if="selectedPromotion.activities && selectedPromotion.activities.length > 0">
                <div class="col-md-12">
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Activity History</h6>
                    </div>
                    <div class="card-body">
                      <div class="promotion-activity-timeline">
                        <div
                          v-for="(activity, index) in selectedPromotion.activities"
                          :key="index"
                          class="activity-item d-flex mb-3"
                        >
                          <div class="activity-icon me-3">
                            <div
                              class="rounded-circle d-flex align-items-center justify-content-center"
                              :class="getActivityTypeClass(activity.type)"
                              style="width: 36px; height: 36px;"
                            >
                              <i
                                class="mdi"
                                :class="getActivityTypeIcon(activity.type)"
                                style="font-size: 18px;"
                              ></i>
                            </div>
                          </div>
                          <div class="activity-content">
                            <div class="d-flex align-items-center mb-1">
                              <h6 class="mb-0 me-auto">{{ activity.title }}</h6>
                              <small class="text-muted">{{ formatDateTime(activity.timestamp) }}</small>
                            </div>
                            <p class="text-muted mb-0">{{ activity.description }}</p>
                            <small class="text-muted">By: {{ activity.user }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="closeDetailsModal">Close</button>
        <template v-if="selectedPromotion">
          <button v-if="canApprove(selectedPromotion)" type="button" class="hr-btn hr-btn-success" @click="approvePromotionFromDetails()">
            <i class="bx bx-check"></i> Approve
          </button>
          <button v-if="selectedPromotion.status === 'Pending'" type="button" class="hr-btn hr-btn-danger" @click="rejectPromotionFromDetails()">
            <i class="bx bx-x"></i> Reject
          </button>
          <button v-if="selectedPromotion.status === 'Approved'" type="button" class="hr-btn hr-btn-primary" @click="completePromotionFromDetails()">
            <i class="bx bx-check-double"></i> Complete
          </button>
        </template>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="approvalModal" title="Approve promotion" size="medium">
      <template #body>
            <p>Approve promotion for <strong>{{ selectedPromotion?.employeeName }}</strong>?</p>

            <div class="hr-mb-3">
              <label for="approvalLevel" class="hr-form-label">Approval level</label>
              <select id="approvalLevel" v-model="approvalForm.approvalLevel" class="hr-select" required>
                <option v-for="approval in selectedPromotion?.approvals"
                        :key="approval.id"
                        :value="approval.approvalLevel"
                        :disabled="approval.status !== 'Pending'">
                  {{ approval.approvalLevel }} - {{ approval.approverRole }}
                  {{ approval.status !== 'Pending' ? '(' + approval.status + ')' : '' }}
                </option>
              </select>
            </div>

            <div class="hr-mb-3">
              <label for="approvalComments" class="hr-form-label">Comments</label>
              <textarea id="approvalComments" v-model="approvalForm.comments" class="hr-input" rows="3"></textarea>
            </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="closeApprovalModal">Cancel</button>
        <button type="button" class="hr-btn hr-btn-success" @click="confirmApprovePromotion">
          <i class="bx bx-check"></i> Approve
        </button>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="rejectionModal" title="Reject promotion" size="medium">
      <template #body>
            <p>Reject promotion for <strong>{{ selectedPromotion?.employeeName }}</strong>?</p>

            <div class="hr-mb-3">
              <label for="rejectionReason" class="hr-form-label">Reason <span class="required">*</span></label>
              <textarea id="rejectionReason" v-model="rejectionForm.rejectionReason" class="hr-input" rows="3" required></textarea>
            </div>

            <div class="hr-mb-3" v-if="selectedPromotion?.approvals && selectedPromotion.approvals.length > 0">
              <label for="rejectionApprovalLevel" class="hr-form-label">Approval level</label>
              <select id="rejectionApprovalLevel" v-model="rejectionForm.approvalLevel" class="hr-select">
                <option value="">Overall Rejection</option>
                <option v-for="approval in selectedPromotion.approvals"
                        :key="approval.id"
                        :value="approval.approvalLevel"
                        :disabled="approval.status !== 'Pending'">
                  {{ approval.approvalLevel }} - {{ approval.approverRole }}
                </option>
              </select>
              <small class="hr-text-muted">Leave blank for overall rejection</small>
            </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="closeRejectionModal">Cancel</button>
        <button type="button" class="hr-btn hr-btn-danger" @click="confirmRejectPromotion">
          <i class="bx bx-x"></i> Reject
        </button>
      </template>
    </ModalWrapper>

    <ModalWrapper ref="completeModal" title="Complete promotion" size="medium">
      <template #body>
            <div class="promo-info-banner">
              <i class="bx bx-info-circle"></i>
              This updates the employee record with the new position, department, and salary.
            </div>
            <p class="hr-mt-3">Complete promotion for <strong>{{ selectedPromotion?.employeeName }}</strong>?</p>
            <div class="hr-mb-3">
              <label for="completionComments" class="hr-form-label">Comments</label>
              <textarea id="completionComments" v-model="completionForm.comments" class="hr-input" rows="3"></textarea>
            </div>
      </template>
      <template #footer>
        <button type="button" class="hr-btn hr-btn-secondary" @click="closeCompleteModal">Cancel</button>
        <button type="button" class="hr-btn hr-btn-primary" @click="confirmCompletePromotion">
          <i class="bx bx-check-double"></i> Complete
        </button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "./HrPageLayout.vue";
import ModalWrapper from "./ModalWrapper.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "HRPromotions",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      revealedSalaries: {},
      revealDuration: 3000,
      submitLoading: false,
      loading: false,
      promotions: [],
      upcomingPromotions: [],
      departments: [],
      employees: [],
      eligibleEmployees: [],
      employeeSearch: "",
      employeeSearchLoading: false,
      employeeSearchTimer: null,
      promotionTypes: ['Regular', 'Performance-Based', 'Probation Completion', 'Exceptional', 'Internal Transfer', 'Restructuring', 'Project Completion', 'Skills Upgrade', 'Other'],
      filters: {
        departmentId: "",
        promotionType: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(90),
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalPromotions: 0,
        limit: 10
      },
      metrics: {
        totalPromotions: 0,
        pendingPromotions: 0,
        approvedPromotions: 0,
        rejectedPromotions: 0,
        upcomingPromotions: 0,
        typeCounts: {}
      },
      selectedPromotion: null,
      promotionForm: {
        employeeId: "",
        previousDesignation: "",
        newDesignation: "",
        previousDepartmentId: "",
        previousDepartmentName: "",
        newDepartmentId: "",
        previousSalary: "",
        newSalary: "",
        promotionDate: new Date().toISOString().split('T')[0],
        effectiveDate: new Date().toISOString().split('T')[0],
        reason: "",
        recommendations: "",
        promotionType: "Regular",
        performanceRating: "",
        documentsList: []
      },
      isEditMode: false,
      selectedEmployee: null,
      removedDocuments: [],
      pendingFiles: [],
      approvalForm: {
        approvalLevel: "",
        comments: ""
      },
      rejectionForm: {
        rejectionReason: "",
        approvalLevel: ""
      },
      completionForm: {
        comments: ""
      }
    };
  },
  mounted() {
    this.getDepartments();
    this.getPromotions();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getPromotions() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getPromotions");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.promotionType) {
        data.append("promotionType", this.filters.promotionType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.promotions = res.data.data.promotions || [];
              this.metrics = res.data.data.metrics || {};
              this.upcomingPromotions = res.data.data.upcomingPromotions || [];
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalPromotions: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching promotions:", res.data.message);
              this.showToast("error", "Failed to load promotions data: " + res.data.message);
              this.promotions = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching promotions");
            this.promotions = [];
            this.metrics = {};
          });
    },

    getEligibleEmployees(search = "") {
      this.employeeSearchLoading = true;
      const data = new FormData();
      data.append("function", "getEligibleEmployees");
      const query = (search || this.employeeSearch || "").trim();
      if (query) {
        data.append("search", query);
      }

      return execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.eligibleEmployees = res.data.data?.eligibleEmployees || [];
            } else {
              this.eligibleEmployees = [];
              console.error("Error fetching eligible employees:", res.data.message);
              if (!query) {
                this.showToast("error", "Failed to load eligible employees");
              }
            }
          })
          .catch((error) => {
            this.eligibleEmployees = [];
            console.error("API error:", error);
            if (!query) {
              this.showToast("error", "Network error when fetching eligible employees");
            }
          })
          .finally(() => {
            this.employeeSearchLoading = false;
          });
    },

    debouncedEmployeeSearch() {
      if (this.promotionForm.employeeId) {
        this.promotionForm.employeeId = "";
        this.selectedEmployee = null;
      }
      clearTimeout(this.employeeSearchTimer);
      this.employeeSearchTimer = setTimeout(() => {
        this.getEligibleEmployees(this.employeeSearch);
      }, 300);
    },

    onEmployeeSearchFocus() {
      if (!this.eligibleEmployees.length) {
        this.getEligibleEmployees(this.employeeSearch);
      }
    },

    applySelectedEmployee(employee) {
      if (!employee) {
        this.selectedEmployee = null;
        return;
      }

      this.selectedEmployee = employee;
      this.promotionForm.previousDesignation = employee.designation;
      this.promotionForm.previousDepartmentId = employee.departmentID;
      this.promotionForm.previousDepartmentName = employee.departmentName;
      this.promotionForm.newDepartmentId = employee.departmentID;
      this.promotionForm.previousSalary = employee.salary;

      const currentSalary = parseFloat(employee.salary);
      if (!isNaN(currentSalary)) {
        this.promotionForm.newSalary = Math.round(currentSalary * 1.1);
      }
    },

    selectEligibleEmployee(employee) {
      this.promotionForm.employeeId = employee.id;
      this.employeeSearch = employee.name || "";
      this.applySelectedEmployee(employee);
      this.eligibleEmployees = [];
    },

    clearSelectedEmployee() {
      this.promotionForm.employeeId = "";
      this.promotionForm.previousDesignation = "";
      this.promotionForm.previousDepartmentId = "";
      this.promotionForm.previousDepartmentName = "";
      this.promotionForm.newDepartmentId = "";
      this.promotionForm.previousSalary = "";
      this.promotionForm.newSalary = "";
      this.selectedEmployee = null;
      this.employeeSearch = "";
      this.getEligibleEmployees();
    },

    refreshData() {
      this.getPromotions();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        promotionType: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(90),
        search: "",
        page: 1,
        limit: 10
      };
      this.getPromotions();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getPromotions();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    openPromotionModal() {
      this.$nextTick(() => this.$refs.promotionModal?.open());
    },

    closePromotionModal() {
      this.$refs.promotionModal?.close();
    },

    openDetailsModal() {
      this.$nextTick(() => this.$refs.detailsModal?.open());
    },

    closeDetailsModal() {
      this.$refs.detailsModal?.close();
    },

    openApprovalModal() {
      this.$nextTick(() => this.$refs.approvalModal?.open());
    },

    closeApprovalModal() {
      this.$refs.approvalModal?.close();
    },

    openRejectionModal() {
      this.$nextTick(() => this.$refs.rejectionModal?.open());
    },

    closeRejectionModal() {
      this.$refs.rejectionModal?.close();
    },

    openCompleteModal() {
      this.$nextTick(() => this.$refs.completeModal?.open());
    },

    closeCompleteModal() {
      this.$refs.completeModal?.close();
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').filter(Boolean).map(n => n[0]).slice(0, 2).join('').toUpperCase();
    },

    createPromotion() {
      this.isEditMode = false;
      this.promotionForm = {
        employeeId: "",
        previousDesignation: "",
        newDesignation: "",
        previousDepartmentId: "",
        previousDepartmentName: "",
        newDepartmentId: "",
        previousSalary: "",
        newSalary: "",
        promotionDate: new Date().toISOString().split('T')[0],
        effectiveDate: new Date().toISOString().split('T')[0],
        reason: "",
        recommendations: "",
        promotionType: "Regular",
        performanceRating: "",
        documentsList: []
      };

      this.selectedEmployee = null;
      this.removedDocuments = [];
      this.pendingFiles = [];
      this.employeeSearch = "";
      this.eligibleEmployees = [];
      clearTimeout(this.employeeSearchTimer);

      this.getEligibleEmployees();
      this.openPromotionModal();
    },

    editPromotion(promotion) {
      this.isEditMode = true;
      this.selectedPromotion = promotion;

      this.promotionForm = {
        promotionId: promotion.id,
        employeeId: promotion.employeeId,
        previousDesignation: promotion.previousDesignation,
        newDesignation: promotion.newDesignation,
        previousDepartmentId: promotion.previousDepartmentId,
        previousDepartmentName: promotion.previousDepartmentName,
        newDepartmentId: promotion.newDepartmentId || promotion.previousDepartmentId,
        previousSalary: promotion.previousSalary,
        newSalary: promotion.newSalary,
        promotionDate: promotion.promotionDate,
        effectiveDate: promotion.effectiveDate,
        reason: promotion.reason,
        recommendations: promotion.recommendations,
        promotionType: promotion.promotionType,
        performanceRating: promotion.performanceRating,
        documentsList: promotion.documentsList || []
      };

      this.selectedEmployee = {
        id: promotion.employeeId,
        name: promotion.employeeName,
        designation: promotion.previousDesignation,
        departmentName: promotion.previousDepartmentName,
        salary: promotion.previousSalary
      };

      this.removedDocuments = [];
      this.pendingFiles = [];

      this.openPromotionModal();
    },

    handleFileSelection(event) {
      this.pendingFiles = event.target.files;
    },

    removeDocument(doc, index) {
      this.removedDocuments.push(doc.path);
      this.promotionForm.documentsList.splice(index, 1);
    },

    async submitPromotion() {
      if (!this.validatePromotionForm()) {
        return;
      }

      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updatePromotion");
        data.append("promotionId", this.promotionForm.promotionId);
      } else {
        data.append("function", "createPromotion");
      }

      data.append("employeeId", this.promotionForm.employeeId);
      data.append("previousDesignation", this.promotionForm.previousDesignation);
      data.append("newDesignation", this.promotionForm.newDesignation);
      data.append("previousDepartmentId", this.promotionForm.previousDepartmentId);
      data.append("newDepartmentId", this.promotionForm.newDepartmentId);
      data.append("previousSalary", this.promotionForm.previousSalary);
      data.append("newSalary", this.promotionForm.newSalary);
      data.append("promotionDate", this.promotionForm.promotionDate);
      data.append("effectiveDate", this.promotionForm.effectiveDate);
      data.append("reason", this.promotionForm.reason);
      data.append("recommendations", this.promotionForm.recommendations);
      data.append("promotionType", this.promotionForm.promotionType);
      data.append("performanceRating", this.promotionForm.performanceRating);

      if (this.pendingFiles.length > 0) {
        for (let i = 0; i < this.pendingFiles.length; i++) {
          data.append(`document_${i}`, this.pendingFiles[i]);
        }
      }

      if (this.isEditMode && this.removedDocuments.length > 0) {
        data.append("removedDocuments", JSON.stringify(this.removedDocuments));
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closePromotionModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when saving promotion");
          });
    },

    validatePromotionForm() {
      if (!this.promotionForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return false;
      }

      if (!this.promotionForm.newDesignation) {
        this.showToast("error", "New designation is required");
        return false;
      }

      if (!this.promotionForm.newSalary) {
        this.showToast("error", "New salary is required");
        return false;
      }

      if (!this.promotionForm.reason) {
        this.showToast("error", "Reason for promotion is required");
        return false;
      }

      return true;
    },

    viewPromotionDetails(promotion) {
      this.selectedPromotion = null;
      this.getPromotionDetails(promotion.id);
    },

    approvePromotion(promotion) {
      this.selectedPromotion = promotion;
      this.approvalForm = {
        approvalLevel: "",
        comments: ""
      };

      this.getPromotionDetails(promotion.id, 'approve');
    },

    rejectPromotion(promotion) {
      this.selectedPromotion = promotion;
      this.rejectionForm = {
        rejectionReason: "",
        approvalLevel: ""
      };

      this.getPromotionDetails(promotion.id, 'reject');
    },

    completePromotion(promotion) {
      this.selectedPromotion = promotion;
      this.completionForm = {
        comments: ""
      };

      this.openCompleteModal();
    },

    getPromotionDetails(promotionId, nextAction = null) {
      const data = new FormData();
      data.append("function", "getPromotionDetails");
      data.append("promotionId", promotionId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedPromotion = res.data.data.selectedPromotion;

              if (this.selectedPromotion.approvals && this.selectedPromotion.approvals.length > 0) {
                const pendingApproval = this.selectedPromotion.approvals.find(a => a.status === 'Pending');
                if (pendingApproval) {
                  this.approvalForm.approvalLevel = pendingApproval.approvalLevel;
                }
              }

              if (nextAction === 'approve') {
                this.openApprovalModal();
              } else if (nextAction === 'reject') {
                this.openRejectionModal();
              } else {
                this.openDetailsModal();
              }
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching promotion details");
          });
    },

    confirmApprovePromotion() {
      if (!this.approvalForm.approvalLevel) {
        this.showToast("error", "Please select an approval level");
        return;
      }

      const data = new FormData();
      data.append("function", "approvePromotion");
      data.append("promotionId", this.selectedPromotion.id);
      data.append("approvalLevel", this.approvalForm.approvalLevel);
      data.append("comments", this.approvalForm.comments);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closeApprovalModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when approving promotion");
          });
    },

    confirmRejectPromotion() {
      if (!this.rejectionForm.rejectionReason) {
        this.showToast("error", "Please provide a reason for rejection");
        return;
      }

      const data = new FormData();
      data.append("function", "rejectPromotion");
      data.append("promotionId", this.selectedPromotion.id);
      data.append("rejectionReason", this.rejectionForm.rejectionReason);

      if (this.rejectionForm.approvalLevel) {
        data.append("approvalLevel", this.rejectionForm.approvalLevel);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closeRejectionModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when rejecting promotion");
          });
    },

    confirmCompletePromotion() {
      const data = new FormData();
      data.append("function", "completePromotion");
      data.append("promotionId", this.selectedPromotion.id);
      data.append("comments", this.completionForm.comments);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.closeCompleteModal();
              this.showToast("success", res.data.message);
              this.getPromotions();
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when completing promotion");
          });
    },

    exportPromotions() {
      const data = new FormData();
      data.append("function", "exportPromotionsReport");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.promotionType) {
        data.append("promotionType", this.filters.promotionType);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.getDate();
    },

    getPromotionTypeColor(type) {
      if (!type) return 'hr-badge-muted';
      const typeMap = {
        'Regular': 'hr-badge-primary',
        'Performance-Based': 'hr-badge-success',
        'Probation Completion': 'hr-badge-info',
        'Exceptional': 'hr-badge-warning',
        'Internal Transfer': 'hr-badge-purple',
        'Restructuring': 'hr-badge-info',
        'Project Completion': 'hr-badge-success',
        'Skills Upgrade': 'hr-badge-primary',
        'Other': 'hr-badge-muted'
      };
      return typeMap[type] || 'hr-badge-muted';
    },

    getPromotionTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalPromotions) return 0;
      const count = this.metrics.typeCounts[type] || 0;
      const total = Object.values(this.metrics.typeCounts).reduce((sum, count) => sum + count, 0);
      return ((count / total) * 100).toFixed(1);
    },

    getStatusBadgeClass(status) {
      if (!status) return 'hr-badge-muted';
      const statusMap = {
        'Pending': 'hr-badge-warning',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger',
        'Completed': 'hr-badge-info'
      };
      return statusMap[status] || 'hr-badge-muted';
    },

    getPerformanceRatingColor(rating) {
      if (!rating) return 'bg-secondary';
      if (rating >= 4) return 'bg-success';
      if (rating >= 3) return 'bg-info';
      if (rating >= 2) return 'bg-warning';
      return 'bg-danger';
    },

    getApprovalStatusClass(status, isConnector = false) {
      if (!status) return isConnector ? 'approval-connector-pending' : 'bg-secondary text-white';
      const statusMap = {
        'Pending': isConnector ? 'approval-connector-pending' : 'bg-warning text-white',
        'Approved': isConnector ? 'approval-connector-approved' : 'bg-success text-white',
        'Rejected': isConnector ? 'approval-connector-rejected' : 'bg-danger text-white'
      };
      return statusMap[status] || (isConnector ? 'approval-connector-pending' : 'bg-secondary text-white');
    },

    getApprovalStatusIcon(status) {
      if (!status) return 'mdi-help-circle';
      const statusMap = {
        'Pending': 'mdi-clock-outline',
        'Approved': 'mdi-check-circle',
        'Rejected': 'mdi-close-circle'
      };
      return statusMap[status] || 'mdi-help-circle';
    },

    getActivityTypeClass(type) {
      if (!type) return 'bg-secondary text-white';
      const typeMap = {
        'create': 'bg-primary text-white',
        'update': 'bg-info text-white',
        'approve': 'bg-success text-white',
        'reject': 'bg-danger text-white',
        'complete': 'bg-purple text-white'
      };
      return typeMap[type] || 'bg-secondary text-white';
    },

    getActivityTypeIcon(type) {
      if (!type) return 'mdi-help-circle';
      const typeMap = {
        'create': 'mdi-plus-circle',
        'update': 'mdi-pencil',
        'approve': 'mdi-check-circle',
        'reject': 'mdi-close-circle',
        'complete': 'mdi-check-all'
      };
      return typeMap[type] || 'mdi-help-circle';
    },

    canApprove(promotion) {
      return promotion.status === 'Pending' || promotion.status === 'Approved';
    },

    calculateSalaryIncrease() {
      if (!this.promotionForm.previousSalary || !this.promotionForm.newSalary) {
        return 0;
      }

      const previousSalary = parseFloat(this.promotionForm.previousSalary);
      const newSalary = parseFloat(this.promotionForm.newSalary);

      if (isNaN(previousSalary) || isNaN(newSalary) || previousSalary === 0) {
        return 0;
      }

      return ((newSalary - previousSalary) / previousSalary * 100).toFixed(2);
    },

    calculateSalaryIncreaseFromValues(previous, current) {
      if (!previous || !current) {
        return 0;
      }

      const previousSalary = parseFloat(previous);
      const newSalary = parseFloat(current);

      if (isNaN(previousSalary) || isNaN(newSalary) || previousSalary === 0) {
        return 0;
      }

      return ((newSalary - previousSalary) / previousSalary * 100).toFixed(2);
    },

    calculateServiceYears(hireDate) {
      if (!hireDate) return 0;
      const hireDateTime = new Date(hireDate).getTime();
      const now = new Date().getTime();
      const diffInYears = (now - hireDateTime) / (1000 * 60 * 60 * 24 * 365.25);
      return Math.floor(diffInYears);
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    },

    approvePromotionFromDetails() {
      this.approvalForm = {
        approvalLevel: "",
        comments: ""
      };

      if (this.selectedPromotion.approvals && this.selectedPromotion.approvals.length > 0) {
        const pendingApproval = this.selectedPromotion.approvals.find(a => a.status === 'Pending');
        if (pendingApproval) {
          this.approvalForm.approvalLevel = pendingApproval.approvalLevel;
        }
      }

      this.closeDetailsModal();
      setTimeout(() => {
        this.openApprovalModal();
      }, 300);
    },

    rejectPromotionFromDetails() {
      this.rejectionForm = {
        rejectionReason: "",
        approvalLevel: ""
      };

      this.closeDetailsModal();
      setTimeout(() => {
        this.openRejectionModal();
      }, 300);
    },

    completePromotionFromDetails() {
      this.completionForm = {
        comments: ""
      };

      this.closeDetailsModal();
      setTimeout(() => {
        this.openCompleteModal();
      }, 300);
    },

    toggleSalaryVisibility(salaryId) {
      const id = salaryId || `salary-${Date.now()}`;

      this.revealedSalaries = {
        ...this.revealedSalaries,
        [id]: !this.revealedSalaries[id]
      };

      if (this.revealedSalaries[id]) {
        setTimeout(() => {
          this.revealedSalaries = {
            ...this.revealedSalaries,
            [id]: false
          };
        }, this.revealDuration);
      }
    },

    isSalaryRevealed(salaryId) {
      return !!this.revealedSalaries[salaryId];
    },

    formatSalary(salary, salaryId) {
      if (!salary) return 'N/A';

      if (this.revealedSalaries[salaryId] === undefined) {
        this.revealedSalaries = {
          ...this.revealedSalaries,
          [salaryId]: false
        };
      }

      const formattedValue = typeof salary === 'number'
          ? salary.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
          : salary;

      return formattedValue;
    }
  },
  watch: {
    'promotionForm.employeeId': {
      handler(employeeId) {
        if (!employeeId) {
          this.selectedEmployee = null;
          return;
        }

        if (this.selectedEmployee && String(this.selectedEmployee.id) === String(employeeId)) {
          return;
        }

        const employee = this.eligibleEmployees.find(emp => String(emp.id) === String(employeeId));
        if (employee) {
          this.applySelectedEmployee(employee);
        }
      },
      immediate: true
    },
    'filters.departmentId': function() {
      this.filters.page = 1;
      this.getPromotions();
    },
    'filters.promotionType': function() {
      this.filters.page = 1;
      this.getPromotions();
    },
    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getPromotions();
    }, 500),
    'filters.startDate': function() {
      this.getPromotions();
    },
    'filters.endDate': function() {
      this.getPromotions();
    }
  }
};
</script>

<style scoped>
.promotions-page {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.promo-employee-picker {
  position: relative;
  z-index: 2;
}

.promo-search-hint {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.promo-search-results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 2100;
}

.promo-search-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.promo-search-item:hover {
  background: #f8fafc;
}

.promo-search-item span {
  font-size: 12px;
  color: #64748b;
}

.promo-selected-card {
  padding: 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
}

.promo-selected-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.promo-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
}

.promo-search {
  flex: 1;
  min-width: 200px;
}

.promo-insights-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
}

@media (max-width: 992px) {
  .promo-insights-grid {
    grid-template-columns: 1fr;
  }
}

.promo-type-list {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.promo-type-row {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 0.35rem 0.75rem;
  align-items: center;
}

.promo-type-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.promo-type-count {
  font-weight: 600;
  color: #334155;
  font-size: 0.9rem;
}

.promo-type-row .hr-progress-bar {
  grid-column: 1 / -1;
}

.promo-type-pct {
  font-size: 0.8rem;
  color: #64748b;
  min-width: 2.5rem;
  text-align: right;
}

.promo-upcoming-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.promo-upcoming-item {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fafbfc;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.promo-upcoming-item:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.promo-date-chip {
  flex-shrink: 0;
  width: 52px;
  text-align: center;
  padding: 0.35rem 0.25rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  border: 1px solid #bfdbfe;
}

.promo-date-month {
  display: block;
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #3b82f6;
  font-weight: 600;
}

.promo-date-day {
  display: block;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.1;
  color: #1e40af;
}

.promo-upcoming-body {
  flex: 1;
  min-width: 0;
}

.promo-upcoming-name {
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;
}

.promo-career-path {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  font-size: 0.875rem;
  color: #475569;
}

.promo-career-path i {
  color: #3b82f6;
  font-size: 1.1rem;
}

.promo-career-path.table-path .promo-to {
  font-weight: 600;
  color: #0f172a;
}

.promo-from {
  color: #64748b;
}

.promo-dept {
  display: block;
  margin-top: 0.25rem;
  color: #94a3b8;
  font-size: 0.8rem;
}

.promo-dept-change {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #64748b;
}

.promo-emp-cell {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.promo-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.promo-emp-name {
  font-weight: 600;
  color: #0f172a;
}

.promo-code {
  font-size: 0.72rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}

.promo-dates {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.82rem;
  color: #475569;
}

.promo-dates i {
  margin-right: 0.25rem;
  color: #94a3b8;
}

.promo-effective {
  color: #1d4ed8;
  font-weight: 500;
}

.promo-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.hr-empty-state.compact {
  padding: 2rem 1rem;
}

.hr-empty-state.compact i {
  font-size: 2.5rem;
}

.promo-form .row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -0.5rem;
}

.promo-form .row > [class*="col-"] {
  padding: 0 0.5rem;
  margin-bottom: 1rem;
}

.promo-employee-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
}

.promo-employee-card-title {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.promo-salary-row {
  display: flex;
  gap: 0.5rem;
}

.promo-salary-row .hr-input {
  flex: 1;
}

.promo-salary-up {
  color: #047857;
  font-size: 0.8rem;
}

.promo-doc-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.promo-doc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.promo-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  color: #1e40af;
  font-size: 0.9rem;
}

/* Salary blur styles */
.salary-value {
  position: relative;
  cursor: pointer;
  display: inline-block;
  user-select: none;
}

.salary-value.blurred {
  filter: blur(5px);
  -webkit-filter: blur(5px);
}

.salary-value.blurred:hover {
  filter: blur(4px);
  -webkit-filter: blur(4px);
}

.salary-value::after {
  content: "Click to reveal";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: transparent;
  pointer-events: none;
  transition: color 0.2s ease;
}

.salary-value.blurred::after {
  color: #495057;
}

.salary-value.revealed {
  animation: reveal 0.3s ease-out;
}

@keyframes reveal {
  0% {
    filter: blur(5px);
    -webkit-filter: blur(5px);
  }
  100% {
    filter: blur(0);
    -webkit-filter: blur(0);
  }
}

/* Approval connector styles */
.approval-connector {
  width: 2px;
  height: 40px;
  margin-left: 19px;
  margin-top: 5px;
  margin-bottom: 5px;
}

.approval-connector-pending {
  background-color: #ffc107;
}

.approval-connector-approved {
  background-color: #198754;
}

.approval-connector-rejected {
  background-color: #dc3545;
}

</style>